// oxlint-disable filenames/match-regex
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { collabStateManager } from '@databases/collab-state-manager';
import { useFlags } from '@databases/flags';
import MentionIcon from '@databases/icons/migration/mention';
import type { MessageFormatter } from '@databases/intl/intl-types';
import type { ShowPanelType } from '@databases/panel/PanelContext';
import usePanel from '@databases/panel/usePanel';
import React, { useCallback } from 'react';
// oxlint-disable-next-line no-restricted-imports
import { useIntl } from 'react-intl-next';

import { getConfluenceService } from '@canvas/atlassian-service/confluence';
import { getExperimentValue, isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { manuallyFireExposureEvent } from '@canvas/client-feature-flags/experiments';

import { messages } from './i18n.ts';
import { buildFlagForMentions } from './mentionBuilder.ts';
import { identifyNotificationEligibleUsers } from './notificationEligibleUsers.ts';
import {
  type CreateMentionNotificationProps,
  MentionFlagType,
  type UseMentionNotificationProps
} from './types.ts';

export const createMentionNotification = async ({
  contentId,
  mentions,
  fieldId,
  formatMessage,
  addFlag,
  clearFlags,
  showFlag,
  hideFlag,
  showPanel,
  openFieldSettings,
  analyticsClient,
  isLocked,
  isContextualMenusM2Enabled
}: {
  contentId: string;
  mentions: {
    mentionLocalId: string;
    mentionedUserAccountId: string;
    displayName: string;
  }[];
  fieldId: string;
  formatMessage: MessageFormatter;
  addFlag: (flag: any) => void;
  clearFlags: () => void;
  showFlag: (flag: any) => void;
  hideFlag: (flagId: string) => void;
  showPanel: ShowPanelType;
  openFieldSettings?: (fieldId: string) => void;
  analyticsClient: any;
  isLocked: boolean;
  isContextualMenusM2Enabled: boolean;
}) => {
  const { usersToMention, usersWithoutAccess, usersWithAccess } =
    await identifyNotificationEligibleUsers({
      contentId,
      mentions
    });

  const service = getConfluenceService();

  try {
    await service.createMentionNotification({
      contentId,
      mentions: usersToMention
    });

    const openFieldSettingsWithExposure = (fieldId: string) => {
      manuallyFireExposureEvent('databasesContextualMenusM2');
      if (openFieldSettings) {
        openFieldSettings(fieldId);
      } else {
        showPanel('FieldSettingsPanel', { fieldId });
      }
    };
    const usesBridgeFlags = isFeatureFlagEnabled('database-frontend-flags-over-bridge');
    const dismissFlag = (flagId: string) => {
      if (usesBridgeFlags) {
        hideFlag(flagId);
      } else {
        clearFlags();
      }
    };
    const mentionNotificationDescription = formatMessage(
      isContextualMenusM2Enabled
        ? messages['mention-notification.flag-mention-notification-description']
        : messages['mention-notification.flag-mention-notification-description-legacy']
    );
    const fieldSettingsActionLabel = formatMessage(
      isContextualMenusM2Enabled
        ? messages['mention-notification.flag-edit-field-settings-cta']
        : messages['mention-notification.flag-go-to-field-settings-cta']
    );

    const flagData = buildFlagForMentions({
      formatMessage,
      usersWithAccess,
      usersWithoutAccess,
      isLocked,
      openFieldSettings: openFieldSettingsWithExposure,
      dismissFlag,
      mentionNotificationDescription,
      fieldSettingsActionLabel,
      fieldId,
      analyticsClient,
      contentId
    });

    if (usesBridgeFlags) {
      flagData.forEach(showFlag);
    } else {
      flagData.forEach(addFlag);
    }

    analyticsClient.sendMentionNotificationFlagViewedEvent({
      contentId,
      databaseContainer: getDatabaseContainerType(),
      type: MentionFlagType.SUCCESS
    });
    // eslint-disable-next-line canvas/no-swallow-errors-in-catch
  } catch (err) {
    if (isFeatureFlagEnabled('database-frontend-flags-over-bridge')) {
      showFlag({
        id: 'mention-notification-error-flag',
        title: formatMessage(messages['mention-notification.flag-error-title']),
        description: formatMessage(
          messages['mention-notification.flag-error-description']
        ),
        type: 'custom',
        iconString: 'mention',
        close: 'auto'
      });
    } else {
      addFlag({
        title: formatMessage(messages['mention-notification.flag-error-title']),
        description: formatMessage(
          messages['mention-notification.flag-error-description']
        ),
        icon: () => <MentionIcon label="" />
      });
    }

    analyticsClient.sendMentionNotificationFlagViewedEvent({
      contentId,
      databaseContainer: getDatabaseContainerType(),
      type: MentionFlagType.ERROR
    });
  }
};
type mentionByFields = {
  [key: string]: CreateMentionNotificationProps['mentions'];
};

const mentionsDict: mentionByFields = {};
export const useMentionNotification = ({
  contentId,
  openFieldSettings: openContextualFieldSettings
}: UseMentionNotificationProps) => {
  const { formatMessage } = useIntl();
  const { addFlag, clearFlags, showFlag, hideFlag } = useFlags();
  const { showPanel } = usePanel();
  const openLegacyFieldSettings = useCallback(
    (fieldId: string) => {
      showPanel('FieldSettingsPanel', { fieldId });
    },
    [showPanel]
  );
  const isContextualMenusM2Enabled = getExperimentValue(
    'databasesContextualMenusM2',
    'enabled',
    {
      fireExperimentExposure: false
    }
  );
  const openFieldSettings = openContextualFieldSettings ?? openLegacyFieldSettings;
  const analyticsClient = useAnalytics();
  const collabManager = collabStateManager.getCollabManager(contentId);
  // Users are not allowed to access field settings in a locked structure db
  let isLocked: boolean;
  if (isFeatureFlagEnabled('databasePerfUplift')) {
    isLocked = collabManager?.database.isLocked || false;
  } else {
    const ydb = collabManager?.database.toJSON();
    isLocked = ydb?.isLocked || false;
  }
  const addOrUpdateEntry = (
    key: string,
    value: CreateMentionNotificationProps['mentions']
  ) => {
    mentionsDict[key] = [...(mentionsDict?.[key] || []), ...value];
  };
  const clearFieldMentions = (key: string) => {
    delete mentionsDict[key];
  };

  const flushMentions = async () => {
    const mentionsRequests: Promise<void>[] = [];
    Object.keys(mentionsDict).forEach((fieldId) => {
      if (mentionsDict[fieldId].length > 0) {
        mentionsRequests.push(
          createMentionNotification({
            contentId,
            mentions: [...mentionsDict[fieldId]],
            fieldId,
            formatMessage,
            addFlag,
            clearFlags,
            showFlag,
            hideFlag,
            showPanel,
            openFieldSettings,
            analyticsClient,
            isLocked,
            isContextualMenusM2Enabled
          })
        );
        clearFieldMentions(fieldId);
      }
    });
    await Promise.all(mentionsRequests);
  };

  const scheduleMentionNotification = ({
    mentions: newMentions,
    fieldId
  }: CreateMentionNotificationProps) => {
    addOrUpdateEntry(fieldId, newMentions);
  };

  const removeMentionNotification = (mentionLocalId: string, fieldId: string) => {
    if (mentionsDict[fieldId]) {
      mentionsDict[fieldId] = mentionsDict[fieldId].filter(
        (mention) => mention.mentionLocalId !== mentionLocalId
      );
    }
  };

  const createMentionNotificationFromHook = async ({
    mentions,
    fieldId
  }: CreateMentionNotificationProps) => {
    await createMentionNotification({
      contentId,
      mentions,
      fieldId,
      formatMessage,
      addFlag,
      clearFlags,
      showFlag,
      hideFlag,
      showPanel,
      openFieldSettings,
      analyticsClient,
      isLocked,
      isContextualMenusM2Enabled
    });
  };

  return {
    createMentionNotification: createMentionNotificationFromHook,
    scheduleMentionNotification,
    removeMentionNotification,
    flushMentions
  };
};
